import { apiClient, type ApiResponse, type RequestConfig } from '@shared/api/api-client';
import type {
  LogisticsCatalogItem,
  TravelPlanDay,
  TravelPlanGlobalFeeTemplate,
  TravelPlanPaymentPolicyUpdateRequest,
  TravelPlanPaymentPolicyUpdateResponse,
  TravelPlanningLoadResponse,
} from '@jema-manager/shared';

export const travelPlanningApi = {
  load: async (
    travelCaseId: string,
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<TravelPlanningLoadResponse> => {
    const response = await apiClient.get<ApiResponse<TravelPlanningLoadResponse>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}`,
      undefined,
      config,
    );
    return response.data;
  },

  saveDays: async (travelCaseId: string, days: TravelPlanDay[]) => {
    const response = await apiClient.post<ApiResponse<{ travelCaseId: string }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/days`,
      { days }
    );
    return response.data;
  },

  saveGlobalFees: async (travelCaseId: string, globalFees: TravelPlanGlobalFeeTemplate[]) => {
    const response = await apiClient.post<ApiResponse<{ travelCaseId: string }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/global-fees`,
      { globalFees }
    );
    return response.data;
  },

  finalize: async (travelCaseId: string) => {
    const response = await apiClient.post<
      ApiResponse<{ travelCaseId: string; updatedQuoteIds: string[] }>
    >(`/admin/travel-planning/${encodeURIComponent(travelCaseId)}/finalize`, {});
    return response.data;
  },

  listCatalog: async (
    query?: string,
    config?: Omit<RequestConfig, 'method' | 'body'>
  ) => {
    const response = await apiClient.get<ApiResponse<LogisticsCatalogItem[]>>(
      `/admin/travel-planning/catalog`,
      query ? { query } : undefined,
      config,
    );
    return response.data;
  },

  createCatalogItem: async (payload: {
    label: string;
    description?: string | null;
    category: string;
    amount: number;
    currency: string;
  }) => {
    const response = await apiClient.post<ApiResponse<LogisticsCatalogItem>>(
      `/admin/travel-planning/catalog`,
      payload
    );
    return response.data;
  },

  autoApplyLogistics: async (travelCaseId: string) => {
    const response = await apiClient.post<ApiResponse<{ travelCaseId: string; days: TravelPlanDay[] }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/auto-apply`,
      {}
    );
    return response.data;
  },

  listPlatformDailyFees: async (
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<LogisticsCatalogItem[]> => {
    const response = await apiClient.get<ApiResponse<LogisticsCatalogItem[]>>(
      `/admin/travel-planning/platform-daily-fees`,
      undefined,
      config,
    );
    return response.data;
  },

  updatePaymentPolicy: async (
    travelCaseId: string,
    payload: TravelPlanPaymentPolicyUpdateRequest
  ): Promise<TravelPlanPaymentPolicyUpdateResponse> => {
    const response = await apiClient.post<ApiResponse<TravelPlanPaymentPolicyUpdateResponse>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/payment-policy`,
      payload
    );
    return response.data;
  },
};
